import React, { useState, useEffect } from 'react';
import AdminLayout from '../../components/admin/AdminLayout';
import { useAuth } from '../../context/AuthContext';
import api from '../../utils/api';
import { toast } from 'react-toastify';

const AdminSEO: React.FC = () => {
    const { token } = useAuth();
    
    // SEO States
    const [googleAnalyticsId, setGoogleAnalyticsId] = useState('');
    const [googleTagManagerId, setGoogleTagManagerId] = useState('');
    const [googleSiteVerification, setGoogleSiteVerification] = useState('');
    const [facebookPixelId, setFacebookPixelId] = useState('');
    const [sitemapLastGenerated, setSitemapLastGenerated] = useState<string | null>(null);

    const [loading, setLoading] = useState(true);
    const [saving, setSaving] = useState(false);
    const [generating, setGenerating] = useState(false);

    useEffect(() => {
        const fetchSettings = async () => {
            try {
                const data = await api.getSystemSettings();
                if (data && !data.message) {
                    setGoogleAnalyticsId(data.googleAnalyticsId || '');
                    setGoogleTagManagerId(data.googleTagManagerId || '');
                    setGoogleSiteVerification(data.googleSiteVerification || '');
                    setFacebookPixelId(data.facebookPixelId || '');
                    setSitemapLastGenerated(data.sitemapLastGenerated || null);
                }
            } catch (err) {
                console.error('Failed to fetch SEO settings:', err);
            } finally {
                setLoading(false);
            }
        };

        fetchSettings();
    }, []);

    const handleSave = async () => {
        if (!token) return;
        setSaving(true);
        try {
            const data = await api.updateSystemSettings(token, {
                googleAnalyticsId,
                googleTagManagerId,
                googleSiteVerification,
                facebookPixelId
            });
            if (data && !data.message) {
                toast.success('SEO & Tracking settings updated successfully');
            } else {
                toast.error(data.message || 'Failed to update settings');
            }
        } catch (err) {
            console.error('Failed to update SEO settings:', err);
            toast.error('An error occurred while saving settings');
        } finally {
            setSaving(false);
        }
    };

    const handleGenerateSitemap = async () => {
        if (!token) return;
        setGenerating(true);
        try {
            const data = await api.generateSitemap(token);
            if (data && !data.message.includes('failed')) {
                toast.success('Sitemap generated successfully!');
                setSitemapLastGenerated(new Date().toISOString());
            } else {
                toast.error(data.message || 'Failed to generate sitemap');
            }
        } catch (err) {
            console.error('Sitemap generation failed:', err);
            toast.error('An error occurred while generating sitemap');
        } finally {
            setGenerating(false);
        }
    };

    return (
        <AdminLayout>
            <div className="flex flex-col md:flex-row md:items-center justify-between gap-6 mb-10">
                <div>
                    <h3 className="text-3xl font-bold text-slate-900 dark:text-white leading-none">
                        SEO <span className="text-[#05144b] dark:text-blue-400">& Tracking Management</span>
                    </h3>
                    <p className="text-slate-500 dark:text-slate-400 font-medium text-sm mt-3">
                        Manage search engine optimization, tracking scripts, and sitemap generation.
                    </p>
                </div>
            </div>

            {loading ? (
                <div className="flex flex-col items-center justify-center py-20 bg-white dark:bg-slate-900 rounded-2xl border border-slate-100 dark:border-slate-800 shadow-sm">
                    <div className="w-12 h-12 border-4 border-[#05144b] border-t-transparent rounded-full animate-spin mb-4"></div>
                    <p className="text-slate-400 font-medium text-sm">Loading SEO Console...</p>
                </div>
            ) : (
                <div className="grid grid-cols-1 lg:grid-cols-3 gap-8 mb-20">
                    {/* Main Settings Form */}
                    <div className="lg:col-span-2 space-y-8">
                        <div className="bg-white dark:bg-slate-900 rounded-3xl p-8 md:p-10 border border-slate-100 dark:border-slate-800 shadow-2xl shadow-blue-900/5">
                            <div className="flex items-center gap-3 mb-8 pb-4 border-b border-slate-50 dark:border-slate-800">
                                <div className="p-2.5 rounded-xl bg-blue-50 text-blue-600">
                                    <i className="fa-solid fa-code text-xl"></i>
                                </div>
                                <h4 className="text-xl font-bold text-slate-900 dark:text-white">Tracking Scripts</h4>
                            </div>

                            <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
                                <div className="space-y-2">
                                    <label className="block text-xs font-black text-[#05144b] dark:text-blue-400 uppercase tracking-widest ml-1">Google Analytics ID</label>
                                    <div className="relative group">
                                        <div className="absolute left-4 top-1/2 -translate-y-1/2 text-[#05144b]/60 dark:text-blue-400/60 group-focus-within:text-[#05144b] transition-colors">
                                            <i className="fa-brands fa-google"></i>
                                        </div>
                                        <input 
                                            type="text"
                                            value={googleAnalyticsId}
                                            onChange={(e) => setGoogleAnalyticsId(e.target.value)}
                                            placeholder="G-XXXXXXXXXX"
                                            className="w-full bg-slate-50 dark:bg-slate-800/50 border-none rounded-2xl py-4 pl-12 pr-6 text-sm font-bold text-[#05144b] dark:text-white focus:ring-2 focus:ring-[#05144b] shadow-inner"
                                        />
                                    </div>
                                    <p className="text-[10px] text-black font-medium dark:text-slate-400 ml-1">Primary GA4 measurement ID.</p>
                                </div>

                                <div className="space-y-2">
                                    <label className="block text-xs font-black text-[#05144b] dark:text-blue-400 uppercase tracking-widest ml-1">Google Tag Manager ID</label>
                                    <div className="relative group">
                                        <div className="absolute left-4 top-1/2 -translate-y-1/2 text-[#05144b]/60 dark:text-blue-400/60 group-focus-within:text-[#05144b] transition-colors">
                                            <i className="fa-solid fa-tags"></i>
                                        </div>
                                        <input 
                                            type="text"
                                            value={googleTagManagerId}
                                            onChange={(e) => setGoogleTagManagerId(e.target.value)}
                                            placeholder="GTM-XXXXXXX"
                                            className="w-full bg-slate-50 dark:bg-slate-800/50 border-none rounded-2xl py-4 pl-12 pr-6 text-sm font-bold text-[#05144b] dark:text-white focus:ring-2 focus:ring-[#05144b] shadow-inner"
                                        />
                                    </div>
                                    <p className="text-[10px] text-black font-medium dark:text-slate-400 ml-1">Container ID for all your marketing tags.</p>
                                </div>

                                <div className="space-y-2">
                                    <label className="block text-xs font-black text-[#05144b] dark:text-blue-400 uppercase tracking-widest ml-1">Facebook Pixel ID</label>
                                    <div className="relative group">
                                        <div className="absolute left-4 top-1/2 -translate-y-1/2 text-[#05144b]/60 dark:text-blue-400/60 group-focus-within:text-[#05144b] transition-colors">
                                            <i className="fa-brands fa-facebook"></i>
                                        </div>
                                        <input 
                                            type="text"
                                            value={facebookPixelId}
                                            onChange={(e) => setFacebookPixelId(e.target.value)}
                                            placeholder="XXXXXXXXXXXXXXXX"
                                            className="w-full bg-slate-50 dark:bg-slate-800/50 border-none rounded-2xl py-4 pl-12 pr-6 text-sm font-bold text-[#05144b] dark:text-white focus:ring-2 focus:ring-[#05144b] shadow-inner"
                                        />
                                    </div>
                                    <p className="text-[10px] text-black font-medium dark:text-slate-400 ml-1">Meta Pixel ID for conversion tracking.</p>
                                </div>

                                <div className="space-y-2">
                                    <label className="block text-xs font-black text-[#05144b] dark:text-blue-400 uppercase tracking-widest ml-1">Google Site Verification</label>
                                    <div className="relative group">
                                        <div className="absolute left-4 top-1/2 -translate-y-1/2 text-[#05144b]/60 dark:text-blue-400/60 group-focus-within:text-[#05144b] transition-colors">
                                            <i className="fa-solid fa-check-to-slot"></i>
                                        </div>
                                        <input 
                                            type="text"
                                            value={googleSiteVerification}
                                            onChange={(e) => setGoogleSiteVerification(e.target.value)}
                                            placeholder="Verification string from GSC"
                                            className="w-full bg-slate-50 dark:bg-slate-800/50 border-none rounded-2xl py-4 pl-12 pr-6 text-sm font-bold text-[#05144b] dark:text-white focus:ring-2 focus:ring-[#05144b] shadow-inner"
                                        />
                                    </div>
                                    <p className="text-[10px] text-black font-medium dark:text-slate-400 ml-1">Google Search Console verification meta-tag value.</p>
                                </div>
                            </div>

                            <div className="mt-12 pt-6 border-t border-slate-50 dark:border-slate-800">
                                <button 
                                    onClick={handleSave}
                                    disabled={saving}
                                    className={`w-full py-4 rounded-xl text-sm font-bold tracking-wider transition-all transform active:scale-[0.98] flex items-center justify-center gap-3 ${saving ? 'bg-slate-400 cursor-not-allowed text-white' : 'bg-[#05144b] text-white hover:bg-blue-900 shadow-xl shadow-blue-900/20'}`}
                                >
                                    {saving ? (
                                        <>
                                            <div className="w-5 h-5 border-2 border-white border-t-transparent rounded-full animate-spin"></div>
                                            <span>Applying Rules...</span>
                                        </>
                                    ) : (
                                        <>
                                            <i className="fa-solid fa-cloud-arrow-up text-lg"></i>
                                            <span>Update SEO Configuration</span>
                                        </>
                                    )}
                                </button>
                            </div>
                        </div>
                    </div>

                    {/* Sidebar / Tools */}
                    <div className="space-y-8">
                        {/* Sitemap Generator */}
                        <div className="bg-white dark:bg-slate-900 rounded-3xl p-8 border border-slate-100 dark:border-slate-800 shadow-2xl shadow-blue-900/5 overflow-hidden relative group">
                            <div className="absolute top-0 right-0 w-32 h-32 bg-emerald-500/5 rounded-full -mr-16 -mt-16 transition-transform group-hover:scale-150"></div>
                            
                            <div className="flex items-center gap-3 mb-6">
                                <div className="p-2.5 rounded-xl bg-emerald-50 text-emerald-600">
                                    <i className="fa-solid fa-sitemap text-xl"></i>
                                </div>
                                <h4 className="text-lg font-bold text-slate-900 dark:text-white">Sitemap Engine</h4>
                            </div>

                            <div className="space-y-4 mb-8">
                                <div className="flex items-center justify-between p-4 bg-slate-50 dark:bg-slate-800/50 rounded-2xl">
                                    <span className="text-[10px] font-black text-slate-500 uppercase tracking-widest">Last Run</span>
                                    <span className="text-xs font-bold text-slate-900 dark:text-white">
                                        {sitemapLastGenerated ? new Date(sitemapLastGenerated).toLocaleDateString() : 'Never'}
                                    </span>
                                </div>
                                <p className="text-[11px] font-medium text-slate-500 leading-relaxed px-1">
                                    Regenerate your <span className="font-bold text-[#05144b]">sitemap.xml</span> to include all fresh projects, blogs, and localities for search engines.
                                </p>
                            </div>

                            <button 
                                onClick={handleGenerateSitemap}
                                disabled={generating}
                                className={`w-full py-4 rounded-xl text-xs font-black tracking-[0.2em] uppercase transition-all flex items-center justify-center gap-3 ${generating ? 'bg-slate-100 text-slate-400' : 'bg-emerald-500 text-white hover:bg-emerald-600 shadow-lg shadow-emerald-500/20'}`}
                            >
                                {generating ? (
                                    <>
                                        <div className="w-4 h-4 border-2 border-emerald-500 border-t-transparent rounded-full animate-spin"></div>
                                        <span>Indexing...</span>
                                    </>
                                ) : (
                                    <>
                                        <i className="fa-solid fa-rotate"></i>
                                        <span>Re-Generate Sitemap</span>
                                    </>
                                )}
                            </button>
                        </div>

                        {/* External Tools */}
                        <div className="bg-gradient-to-br from-[#05144b] to-blue-900 rounded-3xl p-8 text-white shadow-2xl shadow-blue-900/20">
                            <h4 className="text-lg font-bold mb-6 text-white">Analytics Shortcuts</h4>
                            <div className="space-y-3">
                                {[
                                    { name: 'Google Search Console', icon: 'fa-solid fa-magnifying-glass', url: 'https://search.google.com/search-console' },
                                    { name: 'GA4 Realtime', icon: 'fa-solid fa-chart-simple', url: 'https://analytics.google.com/' },
                                    { name: 'PageSpeed Insights', icon: 'fa-solid fa-bolt-lightning', url: 'https://pagespeed.web.dev/' },
                                    { name: 'FB Ads Manager', icon: 'fa-brands fa-facebook', url: 'https://adsmanager.facebook.com/' }
                                ].map((link, i) => (
                                    <a 
                                        key={i}
                                        href={link.url}
                                        target="_blank"
                                        rel="noopener noreferrer"
                                        className="flex items-center justify-between p-4 bg-white/10 hover:bg-white/20 rounded-2xl transition-all group"
                                    >
                                        <div className="flex items-center gap-3">
                                            <i className={`${link.icon} text-lg text-white`}></i>
                                            <span className="text-xs font-bold text-white">{link.name}</span>
                                        </div>
                                        <i className="fa-solid fa-arrow-up-right-from-square text-[10px] text-white/50 opacity-0 group-hover:opacity-100 transition-all"></i>
                                    </a>
                                ))}
                            </div>
                        </div>
                    </div>
                </div>
            )}
        </AdminLayout>
    );
};

export default AdminSEO;
